import React, { useState, useMemo, useRef } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TableSortLabel,
  IconButton,
  Tooltip,
  Container,
  Chip,
  Box,
  Typography,
  Snackbar,
  Alert,
} from "@mui/material";
import { Save as SaveIcon, Cancel as CancelIcon, Visibility, Email } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { observer } from "mobx-react-lite";

// Internal imports
import { useStoreByName } from "../../../hooks/use-stores.hooks";
import { ImportClassResponse, Student, FailedUser, AddStudentsToClassRequest } from "@shared/types";
import { addStudentsToClass } from "../../../services/api/teacher.service";
import FailedUsersModal from "../modals/FailedUsersModal";
import { LoaderComponent } from "../core/LoaderComponent";
import IconImportTable from "../../../assets/SVGComponents/IconImportTable";
import ImportExcelModal from '../modals/ImportExcelModal';
import StudentDetailsDrawer from "../modals/StudentDetailsDrawer";

// Assets
import AddUserIcon from '../../../assets/icons/iconAddUser.svg';

// Types
interface AdminStudent {
  id: number;
  name: string;
  email: string;
  role_id?: number;
  role_name?: string;
}

interface AdminStudentsTableWithActionsProps {
  students: AdminStudent[];
  classId: number;
  schoolId: number;
  className?: string;
  schoolName?: string;
  teacherName?: string;
  onStudentsUpdated?: (updatedStudents: AdminStudent[]) => void;
}

type Order = "asc" | "desc";

interface HeadCell {
  id: keyof AdminStudent | "index" | "actions";
  label: string | React.ReactNode;
  sortable: boolean;
  width: string;
}

/**
 * AdminStudentsTableWithActions Component
 * מציג טבלת תלמידים עם פונקציונליות עריכה עבור האדמין
 */
const AdminStudentsTableWithActions: React.FC<AdminStudentsTableWithActionsProps> = observer(({
  students: initialStudents,
  classId,
  schoolId,
  className,
  schoolName,
  teacherName,
  onStudentsUpdated,
}) => {
  const { t } = useTranslation();
  const teacherStore = useStoreByName("teacherStore");

  // Table State
  const [students, setStudents] = useState<AdminStudent[]>(initialStudents);
  const [orderBy, setOrderBy] = useState<keyof AdminStudent>("name");
  const [order, setOrder] = useState<Order>("asc");
  
  // Add Student State
  const [isAddingStudent, setIsAddingStudent] = useState(false);
  const [newStudent, setNewStudent] = useState({ name: '', email: '' });
  const [isSaving, setIsSaving] = useState(false);
  
  // UI State
  const [isLoading, setIsLoading] = useState(false);
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({ open: false, message: '', severity: 'success' });
  
  // Drawer State
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedStudentId, setSelectedStudentId] = useState<number | null>(null);
  const [selectedStudentName, setSelectedStudentName] = useState<string>("");

  // Failed Users Modal State
  const [failedUsers, setFailedUsers] = useState<FailedUser[]>([]);
  const [showFailedUsersModal, setShowFailedUsersModal] = useState(false);

  const addStudentRowRef = useRef<HTMLTableRowElement | null>(null);

  // Update students when initialStudents changes
  React.useEffect(() => {
    setStudents(initialStudents);
  }, [initialStudents]);

  const scrollToAddStudentRow = () => {
    if (addStudentRowRef.current) {
      addStudentRowRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  };

  // Table Configuration
  const headCells: HeadCell[] = [
    {
      id: "index",
      label: "#",
      sortable: false,
      width: "6%",
    },
    {
      id: "name",
      label: t("studentName"),
      sortable: true,
      width: "32%",
    },
    {
      id: "email",
      label: t("studentEmail"),
      sortable: true,
      width: "32%",
    },
    {
      id: "role_name",
      label: "תפקיד",
      sortable: false,
      width: "18%",
    },
    {
      id: "actions",
      label: !isAddingStudent ? (
        <Container sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', gap: '16px' }}>
          <Tooltip title={t('addStudent')}>
            <IconButton onClick={() => { setIsAddingStudent(true); scrollToAddStudentRow(); }} sx={{width: '34px', height: '34px'  }}>
              <img src={AddUserIcon} alt="Add User" height={26} />
            </IconButton>
          </Tooltip>
          <Tooltip title={t('importTable')}>
            <IconButton 
              onClick={() => setIsImportModalOpen(true)} 
              sx={{p: '5px', width: '34px', height: '34px' }}
            >
              <IconImportTable color="black" />
            </IconButton>
          </Tooltip>
        </Container>
      ) : '',
      sortable: false,
      width: "12%",
    },
  ];

  // Event Handlers
  const handleRequestSort = (property: keyof AdminStudent) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const handleEmailClick = (email: string) => {
    window.open(`mailto:${email}`, "_blank");
  };

  const handleViewStudentDetails = (student: AdminStudent) => {
    setSelectedStudentId(student.id);
    setSelectedStudentName(student.name);
    setDrawerOpen(true);
  };

  const handleCloseDrawer = () => {
    setDrawerOpen(false);
    setSelectedStudentId(null);
    setSelectedStudentName("");
  };

  const handleCancelAdd = () => {
    setIsAddingStudent(false);
    setNewStudent({ name: '', email: '' });
  };

  const handleAddStudent = async () => {
    if (newStudent.name && newStudent.email) {
      setIsSaving(true);
      try {
        const studentData: Student = {
          name: newStudent.name,
          email: newStudent.email,
        };
        
        const response: ImportClassResponse = await teacherStore.addSingleStudentToClass(classId, studentData);
        
        // עדכון רשימת התלמידים
        if (response?.teacherData?.schools) {
          const updatedClass = response.teacherData.schools
            .flatMap(school => school.grades)
            .find(grade => grade.id === classId);
          
          if (updatedClass) {
            const updatedStudents: AdminStudent[] = updatedClass.students.map((s: any) => ({
              id: s.id ?? 0,
              name: s.name ?? '',
              email: s.email,
              role_id: s.role_id,
              role_name: s.role_name,
            }));
            setStudents(updatedStudents);
            onStudentsUpdated?.(updatedStudents);
          }
        }

        setNewStudent({ name: '', email: '' });
        setIsAddingStudent(false);
      } catch (error) {
        console.error("Error adding student:", error);
      } finally {
        setIsSaving(false);
      }
    }
  };

  const handleImportStudentsToClass = async (file: File) => {
    setIsLoading(true);
    try {
      const studentsData: AddStudentsToClassRequest = {
        classId,
        schoolId,
        file,
        name: className || '',
      };
      const response = await addStudentsToClass(studentsData);
      
      if (response.details?.failed && response.details.failed.length > 0) {
        setFailedUsers(response.details.failed);
        setShowFailedUsersModal(true);
      }

      let updated = false;
      if (response?.teacherData?.schools) {
        for (const school of response.teacherData.schools) {
          const updatedClass = school.grades.find((grade: any) => grade.id === classId);
          if (updatedClass) {
            const updatedStudents: AdminStudent[] = updatedClass.students.map((s: any) => ({
              id: s.id ?? 0,
              name: s.name,
              email: s.email,
              role_id: s.role_id,
              role_name: s.role_name,
            }));
            setStudents(updatedStudents);
            updated = true;
            break;
          }
        }
      }

      onStudentsUpdated?.(updated ? students : []);
      setSnackbar({ open: true, message: t('studentsImportedSuccessfully'), severity: 'success' });
    } catch (error) {
      console.error("Error importing students:", error);
      setSnackbar({ open: true, message: t('studentsImportFailed'), severity: 'error' });
    } finally {
      setIsLoading(false);
      setIsImportModalOpen(false);
    }
  };

  // Memoized Sorted Students
  const sortedStudents = useMemo(() => {
    const comparator = (a: AdminStudent, b: AdminStudent) => {
      const aValue = a[orderBy] as string;
      const bValue = b[orderBy] as string;

      if (order === "desc") {
        return bValue.localeCompare(aValue);
      }
      return aValue.localeCompare(bValue);
    };

    return [...students].sort(comparator);
  }, [students, order, orderBy]);

  return (
    <>
      {isLoading && <LoaderComponent />}
      <Box>
        {/* כותרת עם פרטי הכיתה */}
        <Box sx={{ 
          mb: 2, 
          display: "flex", 
          alignItems: "center", 
          justifyContent: "center",
          gap: 2,
          flexWrap: "wrap",
          
        }}>
          <Chip
            label={`${students.length} ${t('students')}`}
            color="primary"
            size="small"
          />
          {className && (
            <Typography variant="body2" color="text.secondary">
              כיתה: {className}
            </Typography>
          )}
          {schoolName && (
            <Typography variant="body2" color="text.secondary">
              בית ספר: {schoolName}
            </Typography>
          )}
          {teacherName && (
            <Typography variant="body2" color="text.secondary">
              {t('teacher')}: {teacherName}
            </Typography>
          )}
        </Box>

        <TableContainer
          component={Paper}
          sx={{
            maxHeight: 400,
            margin: "0 auto",
            boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
            borderRadius: 2,
            "& .MuiTableContainer-root": {
              overflow: "auto",
            },
          }}
        >
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                {headCells.map((headCell) => (
                  <TableCell
                    key={headCell.id}
                    sortDirection={orderBy === headCell.id ? order : false}
                    sx={{
                      width: headCell.width,
                      fontWeight: "bold",
                      backgroundColor: "#f5f5f5",
                    }}
                  >
                    {headCell.sortable ? (
                      <TableSortLabel
                        active={orderBy === headCell.id}
                        direction={orderBy === headCell.id ? order : "asc"}
                        onClick={() =>
                          handleRequestSort(headCell.id as keyof AdminStudent)
                        }
                      >
                        {headCell.label}
                      </TableSortLabel>
                    ) : (
                      headCell.id === 'actions' ? (
                        <Container sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', gap: '16px' }}>
                          <Tooltip title={t('addStudent')}>
                            <IconButton onClick={() => { setIsAddingStudent(true); scrollToAddStudentRow(); }} sx={{width: '34px', height: '34px'  }}>
                              <img src={AddUserIcon} alt="Add User" height={26} />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title={t('importTable')}>
                            <IconButton 
                              onClick={() => setIsImportModalOpen(true)} 
                              sx={{p: '5px', width: '34px', height: '34px' }}
                            >
                              <IconImportTable color="black" />
                            </IconButton>
                          </Tooltip>
                        </Container>
                      ) : headCell.label
                    )}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedStudents.length === 0 && !isAddingStudent && (
                <TableRow>
                  <TableCell colSpan={headCells.length} sx={{ textAlign: 'center', py: 4 }}>
                    <Typography variant="body1" color="text.secondary">
                      {t('noStudentsFound')}
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
              {sortedStudents.map((student, index) => (
                <TableRow
                  key={student.id}
                  sx={{
                    "&:nth-of-type(odd)": {
                      backgroundColor: "#fafafa",
                    },
                    "&:hover": {
                      backgroundColor: "#f0f0f0",
                    },
                  }}
                >
                  <TableCell sx={{ textAlign: "center", fontWeight: "bold" }}>
                    {index + 1}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {student.name}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {student.email}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={student.role_name || "לא ידוע"}
                      size="small"
                      color={
                        student.role_id === 1 
                          ? "primary" 
                          : student.role_id === 2 
                          ? "success" 
                          : student.role_id === 3 
                          ? "warning" 
                          : "default"
                      }
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: "flex", gap: 1 }}>
                      <Tooltip title="שלח אימייל">
                        <IconButton
                          size="small"
                          onClick={() => handleEmailClick(student.email)}
                          sx={{
                            color: "#5898ff",
                            "&:hover": {
                              backgroundColor: "rgba(88, 152, 255, 0.1)",
                            },
                          }}
                        >
                          <Email fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="צפה בפרטים">
                        <IconButton
                          size="small"
                          sx={{
                            color: "#28a745",
                            "&:hover": {
                              backgroundColor: "rgba(40, 167, 69, 0.1)",
                            },
                          }}
                          onClick={() => handleViewStudentDetails(student)}
                        >
                          <Visibility fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
              {isAddingStudent && (
                <TableRow className="new-student-row" ref={addStudentRowRef}>
                  <TableCell>{sortedStudents.length + 1}</TableCell>
                  <TableCell>
                    <input
                      value={newStudent.name}
                      onChange={(e) => setNewStudent({ ...newStudent, name: e.target.value })}
                      placeholder={t('enterName')}
                      style={{
                        width: '100%',
                        padding: '8px',
                        borderRadius: '4px',
                        border: '1px solid #ccc'
                      }}
                      disabled={isSaving}
                    />
                  </TableCell>
                  <TableCell>
                    <input
                      value={newStudent.email}
                      onChange={(e) => setNewStudent({ ...newStudent, email: e.target.value })}
                      placeholder={t('enterEmail')}
                      style={{
                        width: '100%',
                        padding: '8px',
                        borderRadius: '4px',
                        border: '1px solid #ccc'
                      }}
                      disabled={isSaving}
                    />
                  </TableCell>
                  <TableCell></TableCell>
                  <TableCell align="right">
                    {!isSaving && (
                      <>
                        <IconButton
                          onClick={handleAddStudent}
                          color="primary"
                          disabled={!newStudent.name || !newStudent.email}
                        >
                          <SaveIcon />
                        </IconButton>
                        <IconButton onClick={handleCancelAdd}>
                          <CancelIcon />
                        </IconButton>
                      </>
                    )}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        
        {/* Failed Users Modal */}
        <FailedUsersModal
          isOpen={showFailedUsersModal}
          onClose={() => setShowFailedUsersModal(false)}
          failedUsers={failedUsers}
        />

        {/* Import Excel Modal */}
        <ImportExcelModal
          isModalOpen={isImportModalOpen}
          setIsModalOpen={setIsImportModalOpen}
          handleImportStudents={handleImportStudentsToClass}
        />
        
        {/* Student Details Drawer */}
        <StudentDetailsDrawer
          open={drawerOpen}
          onClose={handleCloseDrawer}
          studentId={selectedStudentId}
          studentName={selectedStudentName}
        />

        <Snackbar
          open={snackbar.open}
          autoHideDuration={4000}
          onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert
            onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
            severity={snackbar.severity}
            variant="filled"
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Box>
    </>
  );
});

export default AdminStudentsTableWithActions;

